/**
 * 天候のオーバーレイ（docs/05_rendering-and-audio.md 9 章）：
 * 成熟した雷雲の範囲（赤い円）と、予報レイヤー（雷雨リスクの地域・稜線の高さの風の矢印。L キー）。
 */
import * as THREE from 'three';
import { CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';
import type { Forecast } from '../../sim/weather/forecast';
import type { Storm } from '../../sim/weather/model';
import { drapedCircle, DynamicLine, type HeightFn } from './groundShapes';

const ARROW_Y = 2700;
const GRID_M = 10_000;

export class WeatherOverlays {
  readonly group = new THREE.Group();
  readonly forecastGroup = new THREE.Group();
  private readonly stormLines: DynamicLine[] = [];
  private readonly riskLine: DynamicLine;
  private readonly riskLabel: CSS2DObject;
  private readonly arrows: THREE.LineSegments;
  private arrowKey = '';

  constructor(
    private readonly height: HeightFn,
    private readonly bounds: { widthM: number; depthM: number },
  ) {
    const stormMat = new THREE.LineBasicMaterial({ color: 0xef4444, depthTest: false, transparent: true });
    for (let i = 0; i < 3; i++) {
      const l = new DynamicLine(stormMat, 13);
      this.stormLines.push(l);
      this.group.add(l.line);
    }
    this.riskLine = new DynamicLine(
      new THREE.LineDashedMaterial({
        color: 0xf97316,
        dashSize: 900,
        gapSize: 600,
        depthTest: false,
        transparent: true,
      }),
      13,
    );
    const el = document.createElement('div');
    el.className = 'world-label world-label--risk';
    this.riskLabel = new CSS2DObject(el);
    this.arrows = new THREE.LineSegments(
      new THREE.BufferGeometry(),
      new THREE.LineBasicMaterial({ color: 0x93c5fd, depthTest: false, transparent: true, opacity: 0.85 }),
    );
    this.arrows.renderOrder = 13;
    this.arrows.frustumCulled = false;
    this.forecastGroup.add(this.riskLine.line, this.riskLabel, this.arrows);
    this.group.add(this.forecastGroup);
  }

  setForecastVisible(v: boolean): void {
    this.forecastGroup.visible = v;
    this.riskLabel.element.hidden = !v;
  }

  update(storms: Storm[], forecast: Forecast, timeOfDayS: number): void {
    // 成熟した雷雲の範囲
    const active = storms.filter(
      (s) => (s.phase === 'mature' || s.phase === 'dissipating') && s.radiusM > 200,
    );
    this.stormLines.forEach((l, i) => {
      const s = active[i];
      l.set(s ? drapedCircle(s.x, s.z, s.radiusM, this.height, 60) : null);
    });

    // 予報：雷雨リスクの地域
    const area = forecast.storm.area;
    const key = `${forecast.dayIndex}`;
    if (this.arrowKey !== `${key}:${Math.floor(timeOfDayS / 3600)}`) {
      if (area) {
        this.riskLine.set(drapedCircle(area.x, area.z, area.radiusM, this.height, 80, 96));
        this.riskLabel.position.set(area.x, this.height(area.x, area.z) + 1500, area.z);
        const pct = Math.round(forecast.storm.chance * 100);
        const h = (x: number): string => `${Math.floor(x)}時`;
        this.riskLabel.element.textContent = `雷雨 ${pct}%・${h(forecast.storm.fromH)}〜${h(forecast.storm.toH)}`;
      } else {
        this.riskLine.set(null);
        this.riskLabel.element.textContent = '';
      }
      this.buildArrows(forecast, timeOfDayS);
      this.arrowKey = `${key}:${Math.floor(timeOfDayS / 3600)}`;
    }
  }

  /** 稜線の高さの風（予報の、今の時間帯）を 10km 間隔の矢印で */
  private buildArrows(forecast: Forecast, timeOfDayS: number): void {
    const hour = timeOfDayS / 3600;
    const block = forecast.windBlocks.find((b) => hour >= b.fromH && hour < b.toH) ?? forecast.windBlocks[0];
    if (!block) return;
    const from = (block.ridge.fromDeg * Math.PI) / 180;
    // 風は吹いてくる方位の反対へ流れる
    const dx = -Math.sin(from);
    const dz = Math.cos(from);
    const len = 1500 + block.ridge.kmh * 120;
    const pts: number[] = [];
    for (let z = GRID_M / 2; z < this.bounds.depthM; z += GRID_M) {
      for (let x = GRID_M / 2; x < this.bounds.widthM; x += GRID_M) {
        const x0 = x - (dx * len) / 2;
        const z0 = z - (dz * len) / 2;
        const x1 = x + (dx * len) / 2;
        const z1 = z + (dz * len) / 2;
        pts.push(x0, ARROW_Y, z0, x1, ARROW_Y, z1);
        // 矢じり
        const hl = len * 0.3;
        for (const s of [1, -1]) {
          const ang = Math.atan2(dz, dx) + Math.PI + s * 0.45;
          pts.push(x1, ARROW_Y, z1, x1 + Math.cos(ang) * hl, ARROW_Y, z1 + Math.sin(ang) * hl);
        }
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pts), 3));
    this.arrows.geometry.dispose();
    this.arrows.geometry = geo;
  }
}
